import { WORLD_MAX_Y, WORLD_MIN_Y } from '../constants.ts';
import { blockOf, collides, isLiquid, blockNameOf, AIR_STATE } from '../registry/blocks.ts';
import { collisionBoxes } from '../registry/shapes.ts';

/**
 * Entity movement and collision.
 *
 * The same code runs on the client (for prediction) and on the server (as the
 * authority), so a well-behaved client's predicted position matches the
 * server's to the bit and no correction packet is ever needed. Constants follow
 * vanilla: 0.08 gravity, 0.98 vertical drag, 0.91 horizontal drag times the
 * block's slipperiness, and the cubed friction term in ground acceleration
 * that makes ice feel the way it does.
 */

export interface BlockSource {
  getState(x: number, y: number, z: number): number;
}

export interface Aabb {
  minX: number; minY: number; minZ: number;
  maxX: number; maxY: number; maxZ: number;
}

export interface PhysicsEntity {
  x: number; y: number; z: number;
  vx: number; vy: number; vz: number;
  width: number;
  height: number;
  onGround: boolean;
  /** Set when the entity is standing in a climbable block. */
  climbing: boolean;
  inWater: boolean;
  inLava: boolean;
  /** Ticks since the entity last touched the ground, for fall damage. */
  fallDistance: number;
  /** Set when the entity is not affected by gravity or collisions. */
  flying?: boolean;
  noClip?: boolean;
}

export const GRAVITY = 0.08;
export const VERTICAL_DRAG = 0.98;
export const AIR_DRAG = 0.91;
export const DEFAULT_SLIPPERINESS = 0.6;
export const JUMP_VELOCITY = 0.42;
export const WALK_SPEED = 0.1;
export const SPRINT_MULTIPLIER = 1.3;
export const SNEAK_MULTIPLIER = 0.3;
export const AIR_ACCELERATION = 0.02;
export const WATER_DRAG = 0.8;
export const LAVA_DRAG = 0.5;
export const STEP_HEIGHT = 0.6;

export function entityBox(e: PhysicsEntity): Aabb {
  const half = e.width / 2;
  return {
    minX: e.x - half, minY: e.y, minZ: e.z - half,
    maxX: e.x + half, maxY: e.y + e.height, maxZ: e.z + half,
  };
}

function expandBox(b: Aabb, dx: number, dy: number, dz: number): Aabb {
  return {
    minX: dx < 0 ? b.minX + dx : b.minX,
    minY: dy < 0 ? b.minY + dy : b.minY,
    minZ: dz < 0 ? b.minZ + dz : b.minZ,
    maxX: dx > 0 ? b.maxX + dx : b.maxX,
    maxY: dy > 0 ? b.maxY + dy : b.maxY,
    maxZ: dz > 0 ? b.maxZ + dz : b.maxZ,
  };
}

/** Collect world collision boxes overlapping the given region. */
export function gatherBoxes(world: BlockSource, region: Aabb, out: Aabb[]): Aabb[] {
  out.length = 0;
  const x0 = Math.floor(region.minX - 1);
  const x1 = Math.floor(region.maxX + 1);
  const y0 = Math.max(WORLD_MIN_Y, Math.floor(region.minY - 1));
  const y1 = Math.min(WORLD_MAX_Y - 1, Math.floor(region.maxY + 1));
  const z0 = Math.floor(region.minZ - 1);
  const z1 = Math.floor(region.maxZ + 1);

  for (let y = y0; y <= y1; y++) {
    for (let z = z0; z <= z1; z++) {
      for (let x = x0; x <= x1; x++) {
        const state = world.getState(x, y, z);
        if (state === AIR_STATE || !collides(state)) continue;
        for (const b of collisionBoxes(state)) {
          const box: Aabb = {
            minX: x + b[0], minY: y + b[1], minZ: z + b[2],
            maxX: x + b[3], maxY: y + b[4], maxZ: z + b[5],
          };
          if (
            box.maxX > region.minX && box.minX < region.maxX &&
            box.maxY > region.minY && box.minY < region.maxY &&
            box.maxZ > region.minZ && box.minZ < region.maxZ
          ) {
            out.push(box);
          }
        }
      }
    }
  }
  return out;
}

const scratchBoxes: Aabb[] = [];

/** Axis-by-axis sweep. Returns the movement actually applied. */
export function collideMove(
  world: BlockSource,
  box: Aabb,
  dx: number,
  dy: number,
  dz: number,
): { dx: number; dy: number; dz: number } {
  const region = expandBox(box, dx, dy, dz);
  const boxes = gatherBoxes(world, region, scratchBoxes);
  if (boxes.length === 0) return { dx, dy, dz };

  // Y first, so that landing on a block does not block horizontal movement.
  let ody = dy;
  for (const b of boxes) {
    if (box.maxX <= b.minX || box.minX >= b.maxX) continue;
    if (box.maxZ <= b.minZ || box.minZ >= b.maxZ) continue;
    if (ody > 0 && box.maxY <= b.minY) ody = Math.min(ody, b.minY - box.maxY);
    else if (ody < 0 && box.minY >= b.maxY) ody = Math.max(ody, b.maxY - box.minY);
  }
  const moved: Aabb = { ...box, minY: box.minY + ody, maxY: box.maxY + ody };

  let odx = dx;
  for (const b of boxes) {
    if (moved.maxY <= b.minY || moved.minY >= b.maxY) continue;
    if (moved.maxZ <= b.minZ || moved.minZ >= b.maxZ) continue;
    if (odx > 0 && moved.maxX <= b.minX) odx = Math.min(odx, b.minX - moved.maxX);
    else if (odx < 0 && moved.minX >= b.maxX) odx = Math.max(odx, b.maxX - moved.minX);
  }
  moved.minX += odx;
  moved.maxX += odx;

  let odz = dz;
  for (const b of boxes) {
    if (moved.maxY <= b.minY || moved.minY >= b.maxY) continue;
    if (moved.maxX <= b.minX || moved.minX >= b.maxX) continue;
    if (odz > 0 && moved.maxZ <= b.minZ) odz = Math.min(odz, b.minZ - moved.maxZ);
    else if (odz < 0 && moved.minZ >= b.maxZ) odz = Math.max(odz, b.maxZ - moved.minZ);
  }
  return { dx: odx, dy: ody, dz: odz };
}

/**
 * Move an entity, automatically stepping up over half-blocks the way a player
 * walking into a slab does rather than stopping dead.
 */
export function moveEntity(
  world: BlockSource,
  e: PhysicsEntity,
  dx: number,
  dy: number,
  dz: number,
): void {
  if (e.noClip) {
    e.x += dx;
    e.y += dy;
    e.z += dz;
    return;
  }
  const box = entityBox(e);
  const wasOnGround = e.onGround;
  const result = collideMove(world, box, dx, dy, dz);

  const blockedHorizontally = (result.dx !== dx && dx !== 0) || (result.dz !== dz && dz !== 0);
  if (blockedHorizontally && wasOnGround && dy <= 0) {
    // Try again from STEP_HEIGHT up; if the entity fits, step onto the ledge.
    const stepBox: Aabb = { ...box };
    const up = collideMove(world, stepBox, 0, STEP_HEIGHT, 0);
    if (up.dy > 0.001) {
      const raised: Aabb = {
        minX: box.minX, maxX: box.maxX,
        minY: box.minY + up.dy, maxY: box.maxY + up.dy,
        minZ: box.minZ, maxZ: box.maxZ,
      };
      const stepped = collideMove(world, raised, dx, 0, dz);
      if (Math.abs(stepped.dx) + Math.abs(stepped.dz) > Math.abs(result.dx) + Math.abs(result.dz) + 1e-6) {
        const settled: Aabb = {
          minX: raised.minX + stepped.dx, maxX: raised.maxX + stepped.dx,
          minY: raised.minY, maxY: raised.maxY,
          minZ: raised.minZ + stepped.dz, maxZ: raised.maxZ + stepped.dz,
        };
        const down = collideMove(world, settled, 0, -up.dy, 0);
        e.x += stepped.dx;
        e.y += up.dy + down.dy;
        e.z += stepped.dz;
        e.onGround = true;
        e.fallDistance = 0;
        return;
      }
    }
  }

  e.x += result.dx;
  e.y += result.dy;
  e.z += result.dz;

  if (result.dy !== dy) {
    if (dy < 0) {
      e.onGround = true;
      e.vy = 0;
    } else {
      e.vy = 0;
    }
  } else if (dy !== 0) {
    e.onGround = false;
  }
  if (result.dx !== dx) e.vx = 0;
  if (result.dz !== dz) e.vz = 0;
}

/** Block slipperiness under the entity (ice, slime, soul sand). */
export function slipperinessUnder(world: BlockSource, e: PhysicsEntity): number {
  const y = Math.floor(e.y - 0.2);
  const state = world.getState(Math.floor(e.x), y, Math.floor(e.z));
  return blockOf(state).slipperiness ?? DEFAULT_SLIPPERINESS;
}

export function isInFluid(world: BlockSource, e: PhysicsEntity, fluid: 'water' | 'lava'): boolean {
  const x0 = Math.floor(e.x - e.width / 2);
  const x1 = Math.floor(e.x + e.width / 2);
  const z0 = Math.floor(e.z - e.width / 2);
  const z1 = Math.floor(e.z + e.width / 2);
  const y0 = Math.floor(e.y);
  const y1 = Math.floor(e.y + e.height * 0.7);
  for (let y = y0; y <= y1; y++) {
    for (let z = z0; z <= z1; z++) {
      for (let x = x0; x <= x1; x++) {
        const state = world.getState(x, y, z);
        if (!isLiquid(state)) continue;
        if (blockNameOf(state) === fluid) return true;
      }
    }
  }
  return false;
}

export function isOnLadder(world: BlockSource, e: PhysicsEntity): boolean {
  const state = world.getState(Math.floor(e.x), Math.floor(e.y), Math.floor(e.z));
  const n = blockNameOf(state);
  return n === 'ladder' || n === 'vine';
}

export interface MoveInput {
  /** -1..1 strafe (right positive) and forward/back in entity-local space. */
  strafe: number;
  forward: number;
  jump: boolean;
  sneak: boolean;
  sprint: boolean;
  /**
   * Yaw in radians. 0 faces +Z (south) and increases anticlockwise, matching
   * vanilla and the server's `yawToDirection`.
   */
  yaw: number;
  /** Flying/creative vertical input. */
  up?: number;
}

/**
 * One tick of player movement. Kept deliberately close to vanilla's
 * `LivingEntity.travel` so client prediction and server authority agree.
 */
export function tickPlayerPhysics(world: BlockSource, e: PhysicsEntity, input: MoveInput): void {
  e.inWater = isInFluid(world, e, 'water');
  e.inLava = isInFluid(world, e, 'lava');
  e.climbing = isOnLadder(world, e);

  if (e.flying) {
    const speed = input.sprint ? 0.22 : 0.11;
    const [ax, az] = localToWorld(input.strafe, input.forward, input.yaw);
    e.vx += ax * speed;
    e.vz += az * speed;
    e.vy += (input.jump ? 1 : 0) * speed - (input.sneak ? 1 : 0) * speed;
    moveEntity(world, e, e.vx, e.vy, e.vz);
    e.vx *= 0.7;
    e.vy *= 0.7;
    e.vz *= 0.7;
    e.fallDistance = 0;
    return;
  }

  const startY = e.y;

  if (e.inWater) {
    const [ax, az] = localToWorld(input.strafe, input.forward, input.yaw);
    e.vx += ax * 0.02;
    e.vz += az * 0.02;
    if (input.jump) e.vy += 0.04;
    else if (input.sneak) e.vy -= 0.04;
    e.vy -= 0.02;
    moveEntity(world, e, e.vx, e.vy, e.vz);
    e.vx *= WATER_DRAG;
    e.vy *= WATER_DRAG;
    e.vz *= WATER_DRAG;
    e.fallDistance = 0;
    return;
  }

  if (e.inLava) {
    const [ax, az] = localToWorld(input.strafe, input.forward, input.yaw);
    e.vx += ax * 0.02;
    e.vz += az * 0.02;
    if (input.jump) e.vy += 0.04;
    e.vy -= 0.02;
    moveEntity(world, e, e.vx, e.vy, e.vz);
    e.vx *= LAVA_DRAG;
    e.vy *= LAVA_DRAG;
    e.vz *= LAVA_DRAG;
    e.fallDistance = 0;
    return;
  }

  const slipperiness = e.onGround ? slipperinessUnder(world, e) : DEFAULT_SLIPPERINESS;
  const friction = slipperiness * AIR_DRAG;

  // Ground acceleration is scaled so that low-friction blocks accelerate less
  // per tick but retain more speed, which is what makes ice feel slippery
  // rather than simply fast.
  let acceleration: number;
  if (e.onGround) {
    acceleration = WALK_SPEED * (DEFAULT_SLIPPERINESS / slipperiness) ** 3;
    if (input.sprint) acceleration *= SPRINT_MULTIPLIER;
    if (input.sneak) acceleration *= SNEAK_MULTIPLIER;
  } else {
    acceleration = AIR_ACCELERATION;
    if (input.sprint) acceleration *= SPRINT_MULTIPLIER;
  }

  const [ax, az] = localToWorld(input.strafe, input.forward, input.yaw);
  e.vx += ax * acceleration;
  e.vz += az * acceleration;

  if (e.climbing) {
    // Climbing clamps horizontal speed and lets the player hold position.
    e.vx = clampAbs(e.vx, 0.15);
    e.vz = clampAbs(e.vz, 0.15);
    e.vy = Math.max(e.vy, input.sneak ? 0 : -0.15);
    if (input.jump) e.vy = 0.2;
  }

  if (input.jump && e.onGround) {
    e.vy = JUMP_VELOCITY;
    if (input.sprint) {
      // Sprint-jumping gets the usual forward boost, along the facing vector.
      e.vx += -Math.sin(input.yaw) * 0.2;
      e.vz += Math.cos(input.yaw) * 0.2;
    }
    e.onGround = false;
  }

  const wasOnGround = e.onGround;
  e.onGround = false;
  moveEntity(world, e, e.vx, e.vy, e.vz);

  if (!e.onGround) {
    // Re-check for ground: a purely horizontal move does not set onGround.
    const probe = collideMove(world, entityBox(e), 0, -0.001, 0);
    if (probe.dy > -0.001) e.onGround = true;
  }

  e.vy -= GRAVITY;
  e.vy *= VERTICAL_DRAG;
  e.vx *= friction;
  e.vz *= friction;

  if (e.onGround) {
    e.fallDistance = 0;
  } else if (e.y < startY) {
    e.fallDistance += startY - e.y;
  }

  // Kill sub-pixel drift so entities come fully to rest.
  if (Math.abs(e.vx) < 0.003) e.vx = 0;
  if (Math.abs(e.vz) < 0.003) e.vz = 0;
}

/**
 * Convert local strafe/forward input into world X/Z.
 *
 * forward = (-sin yaw, cos yaw), right = (-cos yaw, -sin yaw).
 */
function localToWorld(strafe: number, forward: number, yaw: number): [number, number] {
  const len = Math.hypot(strafe, forward);
  if (len < 1e-4) return [0, 0];
  const s = strafe / Math.max(1, len);
  const f = forward / Math.max(1, len);
  const sin = Math.sin(yaw);
  const cos = Math.cos(yaw);
  return [-f * sin - s * cos, f * cos - s * sin];
}

function clampAbs(v: number, max: number): number {
  return v > max ? max : v < -max ? -max : v;
}

/** Raycast against block collision shapes. Returns the first block hit. */
export interface RayHit {
  x: number; y: number; z: number;
  face: number;
  distance: number;
  /** Exact hit position, for placement offsets. */
  hitX: number; hitY: number; hitZ: number;
}

export function raycast(
  world: BlockSource,
  ox: number, oy: number, oz: number,
  dx: number, dy: number, dz: number,
  maxDistance: number,
  hitFluids = false,
): RayHit | null {
  // Amanatides & Woo voxel traversal, refined against the block's real shape.
  let x = Math.floor(ox);
  let y = Math.floor(oy);
  let z = Math.floor(oz);
  const stepX = dx > 0 ? 1 : dx < 0 ? -1 : 0;
  const stepY = dy > 0 ? 1 : dy < 0 ? -1 : 0;
  const stepZ = dz > 0 ? 1 : dz < 0 ? -1 : 0;
  const tDeltaX = stepX === 0 ? Infinity : Math.abs(1 / dx);
  const tDeltaY = stepY === 0 ? Infinity : Math.abs(1 / dy);
  const tDeltaZ = stepZ === 0 ? Infinity : Math.abs(1 / dz);
  let tMaxX = stepX === 0 ? Infinity : ((stepX > 0 ? x + 1 - ox : ox - x) / Math.abs(dx));
  let tMaxY = stepY === 0 ? Infinity : ((stepY > 0 ? y + 1 - oy : oy - y) / Math.abs(dy));
  let tMaxZ = stepZ === 0 ? Infinity : ((stepZ > 0 ? z + 1 - oz : oz - z) / Math.abs(dz));
  let face = -1;
  let t = 0;

  for (let i = 0; i < 512; i++) {
    if (y >= WORLD_MIN_Y && y < WORLD_MAX_Y) {
      const state = world.getState(x, y, z);
      const solid = hitFluids ? state !== AIR_STATE : collides(state);
      if (solid) {
        const boxes = hitFluids && isLiquid(state) ? [[0, 0, 0, 1, 1, 1] as const] : collisionBoxes(state);
        let best = Infinity;
        let bestFace = face;
        for (const b of boxes) {
          const hit = rayBox(ox, oy, oz, dx, dy, dz, x + b[0], y + b[1], z + b[2], x + b[3], y + b[4], z + b[5]);
          if (hit && hit.t < best && hit.t <= maxDistance) {
            best = hit.t;
            bestFace = hit.face;
          }
        }
        if (best < Infinity) {
          return {
            x, y, z,
            face: bestFace,
            distance: best,
            hitX: ox + dx * best, hitY: oy + dy * best, hitZ: oz + dz * best,
          };
        }
      }
    }
    if (tMaxX < tMaxY && tMaxX < tMaxZ) {
      t = tMaxX;
      if (t > maxDistance) break;
      x += stepX;
      tMaxX += tDeltaX;
      face = stepX > 0 ? 4 : 5; // entered through its west/east face
    } else if (tMaxY < tMaxZ) {
      t = tMaxY;
      if (t > maxDistance) break;
      y += stepY;
      tMaxY += tDeltaY;
      face = stepY > 0 ? 0 : 1;
    } else {
      t = tMaxZ;
      if (t > maxDistance) break;
      z += stepZ;
      tMaxZ += tDeltaZ;
      face = stepZ > 0 ? 2 : 3;
    }
  }
  return null;
}

function rayBox(
  ox: number, oy: number, oz: number,
  dx: number, dy: number, dz: number,
  x0: number, y0: number, z0: number,
  x1: number, y1: number, z1: number,
): { t: number; face: number } | null {
  let tmin = -Infinity;
  let tmax = Infinity;
  let face = -1;

  const check = (o: number, d: number, lo: number, hi: number, negFace: number, posFace: number): boolean => {
    if (Math.abs(d) < 1e-9) return o >= lo && o <= hi;
    const t1 = (lo - o) / d;
    const t2 = (hi - o) / d;
    const near = Math.min(t1, t2);
    const far = Math.max(t1, t2);
    if (near > tmin) {
      tmin = near;
      face = d > 0 ? negFace : posFace;
    }
    tmax = Math.min(tmax, far);
    return tmax >= tmin;
  };

  if (!check(ox, dx, x0, x1, 4, 5)) return null;
  if (!check(oy, dy, y0, y1, 0, 1)) return null;
  if (!check(oz, dz, z0, z1, 2, 3)) return null;
  if (tmax < 0) return null;
  return { t: Math.max(tmin, 0), face };
}
